<template>
    <div ref="feedRoot">
        <p ref="status" class="sr-only" role="status" aria-live="polite" aria-atomic="true" tabindex="-1">{{ announcement }}</p>
        <BaseNotification v-if="feedError" theme="error" :text="t('myWorkInsightFeed.error')" @dismiss="feedError = false" />
        <Transition name="fade" mode="out-in" @after-enter="handleSectionEnter">
            <section
                v-if="dismissed && !bannerExpired"
                key="caught-up"
                ref="dismissedBanner"
                tabindex="-1"
                class="mt-1 flex items-start gap-3 rounded-lgr border border-border bg-surface-raised p-4">
                <i class="text-lg/none mt-1 bi bi-lightbulb text-ink-muted" aria-hidden="true"></i>
                <p v-if="feed?.exhausted" class="body text-ink-muted">{{ t("myWorkInsightFeed.exhausted") }}</p>
                <p v-else class="body text-ink-muted">
                    You're all caught up. Your My Work insights are always available on the
                    <BaseLink :href="MY_WORK_URL" class="body-semibold text-button-tertiary-text hover:underline">My Work page</BaseLink>.
                </p>
            </section>

            <section v-else-if="!dismissed && cards.length" key="carousel">
                <HomeSectionHeader title="Some food for thought">
                    <template #actions>
                        <span class="inline-flex shrink-0 flex-col items-end gap-1 details text-ink-muted sm:flex-row sm:items-center sm:gap-2">
                            <span class="whitespace-nowrap">{{ index + 1 }} of {{ cards.length }}</span>
                            <span class="hidden sm:inline" aria-hidden="true">·</span>
                            <BaseButton
                                theme="tertiary"
                                size="sm"
                                class="details relative inline-flex items-center justify-center gap-2 p-0! whitespace-nowrap text-button-tertiary-text hover:underline"
                                :disabled="isSeeingFullInsight"
                                :aria-busy="isSeeingFullInsight"
                                @click="handleSeeAll">
                                <LoadingSpinner v-if="isSeeingFullInsight" class="absolute h-3.5 w-3.5" />
                                <span :class="{ invisible: isSeeingFullInsight }">See full insight</span>
                            </BaseButton>
                        </span>
                    </template>
                </HomeSectionHeader>

                <BaseCarousel
                    v-model="activeInsightId"
                    :item-ids="cards.map((card) => card.insightId)"
                    :label="t('home.insights.carouselLabel')"
                    :item-label="t('home.insights.itemLabel')"
                    fit-active>
                    <template v-for="card in cards" :key="card.insightId" #[`item-${card.insightId}`]>
                        <Transition name="fade" mode="out-in" appear @after-enter="restoreFocus" @after-appear="restoreFocus">
                            <HomeMyWorkInsightCardRedesigned
                                v-if="card.insightId === activeInsightId"
                                :key="card.insightId"
                                :insight-id="card.insightId"
                                :category="card.category"
                                :experiment-slug="card.experimentSlug"
                                :title="card.title"
                                :summary="card.summary"
                                :show-close="index === cards.length - 1"
                                :evidence-groups="card.evidenceGroups"
                                :initial-feedback-value="card.feedback"
                                :is-starting-chat="isDivingDeeper"
                                :chat-error="chatError"
                                @close="handleClose"
                                @dive-deeper="handleDiveDeeper"
                                @feedback="handleFeedbackGiven" />
                        </Transition>
                    </template>
                </BaseCarousel>
            </section>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import HomeMyWorkInsightCardRedesigned from "~vue/ChatHome/HomeMyWorkInsightCardRedesigned.vue";
import HomeSectionHeader from "~vue/ChatHome/HomeSectionHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { useInsightFeedAccessibility } from "~vue/composables/useInsightFeedAccessibility";
import { useLabInsightChat } from "~vue/composables/useLabInsightChat";
import { useMyWorkInsightFeed } from "~vue/composables/useMyWorkInsightFeed";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { useUIInteractions } from "~vue/composables/useUIInteractions";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { HomepageMyWorkInsight } from "~vue/types/chat_home";
import type { LabInsightFeedbackResponse, MyWorkInsightFeed } from "~vue/types/labs";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, ref, shallowRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const MY_WORK_URL = "/intelligence/my-work/";
const MAX_CARDS = 3;
const DISMISSED_BANNER_MS = 5000;

// Insights arrive as an Inertia prop (server-rendered by chat_home), not a client-side fetch.
const props = withDefaults(
    defineProps<{
        insights?: HomepageMyWorkInsight[];
        initialFeed?: MyWorkInsightFeed | null;
    }>(),
    {
        insights: () => [],
        initialFeed: null,
    },
);

const emit = defineEmits<{ retire: [] }>();

interface CarouselCard {
    insightId: string;
    category: string;
    experimentSlug: string;
    title: string;
    summary: string;
    createdAt: string | null;
    evidenceGroups: HomepageMyWorkInsight["evidence_groups"];
    feedback: HomepageMyWorkInsight["feedback"];
}

const { t } = useI18n();
const { feed, error: feedError, saving } = useMyWorkInsightFeed(() => props.initialFeed);
const hiddenInsightIds = ref(new Set<string>());
const feedInsights = computed<HomepageMyWorkInsight[]>(() =>
    feed.value?.feed_enabled
        ? feed.value.experiments.flatMap((experiment) =>
              experiment.insights
                  .filter((item) => item.insight.made_visible_at != null)
                  .map((item) => ({ ...item.insight, insight_id: item.insight.id, experiment_slug: experiment.slug, experiment_title: experiment.title, feedback: item.feedback })),
          )
        : props.insights,
);

const cards = computed<CarouselCard[]>(() =>
    [...feedInsights.value]
        .filter((insight) => !hiddenInsightIds.value.has(insight.insight_id))
        // The card is like/dislike-and-done: an insight that already has feedback (from this
        // visit or a past one) never shows at all, rather than showing an answered row.
        .filter((insight) => insight.feedback == null)
        .sort((a, b) => {
            if (feed.value?.feed_enabled) {
                return (a.order ?? 0) - (b.order ?? 0) || a.insight_id.localeCompare(b.insight_id);
            }
            return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
        })
        .slice(0, MAX_CARDS)
        .map((insight) => ({
            insightId: insight.insight_id,
            category: insight.experiment_title,
            experimentSlug: insight.experiment_slug,
            title: insight.title,
            summary: insight.summary,
            createdAt: insight.created_at,
            evidenceGroups: insight.evidence_groups,
            feedback: insight.feedback,
        })),
);

const index = ref(0);
const feedRoot = shallowRef<HTMLElement | null>(null);
const { announcement, status, restoreFocus, focusAfterRetirement } = useInsightFeedAccessibility(
    feedRoot,
    () => cards.value.map((card) => card.insightId),
    () => Boolean(feed.value?.exhausted),
    () => current.value?.insightId,
);
const current = computed(() => cards.value[index.value]);
const activeInsightId = computed({
    get: () => current.value?.insightId ?? "",
    set: (id: string) => goTo(cards.value.findIndex((card) => card.insightId === id)),
});

const { createInteraction } = useUIInteractions();
const { chatError, handleStartChat } = useLabInsightChat(() => current.value?.insightId ?? "");
const isDivingDeeper = ref(false);

const dismissedBanner = useTemplateRef<HTMLElement>("dismissedBanner");
let bannerFocusOrigin: Element | null = null;
const dismissalReason = ref<"explicit" | "exhausted" | null>(feed.value?.exhausted ? "exhausted" : null);
const dismissed = computed(() => dismissalReason.value !== null);
const bannerExpired = ref(false);
const isSeeingFullInsight = ref(false);
const { isShowing: bannerShowing, show: showDismissedBanner } = useTimedShow({ duration: DISMISSED_BANNER_MS });
watch(bannerShowing, (now, previous) => {
    if (previous && !now && dismissed.value) {
        bannerExpired.value = true;
        if (dismissedBanner.value?.contains(document.activeElement) || document.activeElement === status.value) {
            focusAfterRetirement();
        }
        if (!saving.value) {
            emit("retire");
        }
    }
});

watch(saving, (now) => {
    if (!now && bannerExpired.value && dismissed.value && !feedError.value) {
        emit("retire");
    }
});

watch(
    cards,
    (value, previous) => {
        if (feed.value?.feed_enabled && previous?.length && !value.length && dismissalReason.value !== "explicit") {
            bannerFocusOrigin = feedRoot.value?.contains(document.activeElement) ? document.activeElement : null;
            dismissalReason.value = "exhausted";
            showDismissedBanner();
        } else if (value.length && dismissalReason.value === "exhausted" && feed.value?.feed_enabled) {
            dismissalReason.value = null;
            bannerExpired.value = false;
        }
        if (index.value > value.length - 1) {
            index.value = 0;
        }
        if (value.length && !previous?.length) {
            logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_SHOWN, {
                insight_count: value.length,
            });
        }
    },
    { immediate: true },
);

function announceCurrentSlide() {
    announcement.value = "";
    void nextTick(() => {
        announcement.value = t("myWorkInsightFeed.slideSelected", {
            position: index.value + 1,
            total: cards.value.length,
            title: current.value.title,
        });
    });
}

function goTo(target: number) {
    if (target < 0 || target >= cards.value.length || target === index.value) {
        return;
    }
    index.value = target;
    announceCurrentSlide();
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_NAVIGATED, {
        to_index: index.value,
    });
}

function handleFeedbackGiven(response?: LabInsightFeedbackResponse) {
    const insightId = response?.insight_id ?? current.value.insightId;
    const currentIndex = index.value;
    bannerFocusOrigin = feedRoot.value?.contains(document.activeElement) ? document.activeElement : null;
    hiddenInsightIds.value = new Set([...hiddenInsightIds.value, insightId]);
    if (cards.value.length === 0) {
        // Every insight in the batch now has feedback: show the same "all caught up" state as
        // an explicit dismissal, rather than the carousel just disappearing.
        if (dismissalReason.value !== "explicit") {
            dismissalReason.value = "exhausted";
        }
        showDismissedBanner();
        return;
    }
    if (currentIndex >= cards.value.length) {
        index.value = 0;
    }
}

if (dismissed.value) {
    showDismissedBanner();
}

// The last-slide X, "See full insight", and Dive Deeper all retire the batch.
async function recordBatchDismissal() {
    await createInteraction("mywork_insight_banner_dismissed");
}

async function handleClose() {
    const active = document.activeElement;
    const shouldMoveFocus = feedRoot.value?.contains(active);
    bannerFocusOrigin = shouldMoveFocus ? active : null;
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_DISMISSED, {
        insight_count: cards.value.length,
    });
    void recordBatchDismissal();
    dismissalReason.value = "explicit";
    showDismissedBanner();
    await nextTick();
    if (shouldMoveFocus && (document.activeElement === active || document.activeElement === document.body)) {
        dismissedBanner.value?.focus({ preventScroll: true });
    }
}

function handleSectionEnter() {
    if (!dismissed.value || bannerExpired.value) {
        return;
    }
    showDismissedBanner();
    if (bannerFocusOrigin && (document.activeElement === bannerFocusOrigin || document.activeElement === document.body || document.activeElement === status.value)) {
        dismissedBanner.value?.focus({ preventScroll: true });
    }
    bannerFocusOrigin = null;
}

async function handleSeeAll() {
    if (isSeeingFullInsight.value) {
        return;
    }
    isSeeingFullInsight.value = true;
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_SEE_ALL_CLICKED, {
        from_index: index.value,
    });
    try {
        await recordBatchDismissal();
    } finally {
        router.visit(MY_WORK_URL);
    }
}

async function handleDiveDeeper() {
    if (isDivingDeeper.value) {
        return;
    }
    isDivingDeeper.value = true;
    const { insightId, experimentSlug } = current.value;
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_CLICKED, {
        insight_id: insightId,
        experiment_slug: experimentSlug,
        location: "mywork_homepage_carousel",
    });
    logUserInteraction(AmplitudeEventNames.INSIGHT_DIVE_DEEPER_CLICKED, { insightId, location: "mywork_homepage_carousel" });
    try {
        await recordBatchDismissal();
        await handleStartChat();
    } finally {
        isDivingDeeper.value = false;
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
    .fade-enter-active,
    .fade-leave-active {
        transition: none;
    }
}
</style>
